import React, { useMemo } from "react";
import { Box, Typography, Tooltip } from "@mui/material";

export function fashionabilityContributionRowKey(row) {
	return (
		String(row?.fashionability ?? "Unknown")
			.trim()
			.replace(/\s+/g, " ")
			.toLowerCase() || "unknown"
	);
}

/** Product grid bucket key — must match aggregation / drilldown. */
export function fashionabilityContributionMapKey(product) {
	if (!product || typeof product !== "object") return "unknown";
	const label = String(product.fashionability ?? "Unknown").trim() || "Unknown";
	return label.replace(/\s+/g, " ").toLowerCase();
}

function formatLabelPct(p) {
	return p % 1 === 0 ? `${Math.round(p)}` : p.toFixed(1);
}

function formatPcs(n) {
	return Math.round(Number(n) || 0).toLocaleString();
}

/**
 * Single straight bar: width = % of sold qty per fashionability, vertical dividers between segments.
 */
export default function FashionabilityContributionRamp({
	rows,
	title = "FASHIONABILITY · PERIOD SOLD QTY %",
	amountLabel = "sold qty",
	unitLabel = "pcs",
	barHeight = 22,
	onSegmentClick,
	selectedFilterKey = null,
}) {
	const segments = useMemo(() => (rows || []).map((r) => ({ ...r })), [rows]);

	const totalQty = useMemo(
		() => segments.reduce((s, r) => s + (Number(r.soldQty) || 0), 0),
		[segments]
	);

	if (!segments.length) return null;

	const titleColor = "#616161";
	const labelColor = "#424242";
	const dividerColor = "#212121";

	return (
		<Box sx={{ width: "100%" }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "baseline",
					justifyContent: "space-between",
					gap: 1,
					mb: 0.75,
					flexWrap: "wrap",
				}}>
				<Typography
					variant="caption"
					component="div"
					sx={{
						color: titleColor,
						fontWeight: 600,
						letterSpacing: "0.07em",
						fontSize: "0.6rem",
						textTransform: "uppercase",
						flex: "1 1 auto",
						minWidth: 0,
					}}>
					{title}
				</Typography>
				<Typography
					variant="caption"
					component="div"
					sx={{
						color: titleColor,
						fontWeight: 600,
						fontSize: "0.6rem",
						flexShrink: 0,
						whiteSpace: "nowrap",
					}}>
					{formatPcs(totalQty)} {unitLabel}
				</Typography>
			</Box>

			<Box
				sx={{
					width: "100%",
					overflow: "hidden",
					borderRadius: "999px",
					bgcolor: "#f5f5f5",
					boxShadow: "inset 0 2px 4px rgba(0,0,0,0.15), inset 0 -2px 4px rgba(255,255,255,0.4)",
				}}>
				<Box
					sx={{
						display: "flex",
						flexDirection: "row",
						alignItems: "stretch",
						width: "100%",
						minWidth: 0,
						height: barHeight,
					}}>
					{segments.map((row, i) => {
						const rowKey = fashionabilityContributionRowKey(row);
						const isSelected = selectedFilterKey != null && selectedFilterKey === rowKey;
						const pct = Number(row.percentage) || 0;
						const qty = Math.round(Number(row.soldQty) || 0);
						const tooltipTitle = `${row.fashionability}: ${pct.toFixed(1)}% of ${amountLabel} · ${qty} ${unitLabel}${
							onSegmentClick ? " · Click to filter grid" : ""
						}`;

						return (
							<Tooltip key={`${rowKey}-${i}`} title={tooltipTitle} arrow>
								<Box
									onClick={
										onSegmentClick
											? (e) => {
													e.stopPropagation();
													onSegmentClick(row);
												}
											: undefined
									}
									role={onSegmentClick ? "button" : undefined}
									tabIndex={onSegmentClick ? 0 : undefined}
									onKeyDown={
										onSegmentClick
											? (e) => {
													if (e.key === "Enter" || e.key === " ") {
														e.preventDefault();
														onSegmentClick(row);
													}
												}
											: undefined
									}
									sx={{
										flex: `0 1 ${pct}%`,
										minWidth: pct > 0 ? 2 : 0,
										height: "100%",
										cursor: onSegmentClick ? "pointer" : "default",
										backgroundColor: isSelected ? "#bdbdbd" : "#e0e0e0",
										borderRight: i < segments.length - 1 ? `1px solid #fff` : "none",
										boxSizing: "border-box",
										transition: "background-color 0.15s ease",
										outline: isSelected ? "2px solid #2e7d32" : "none",
										outlineOffset: isSelected ? 1 : 0,
										zIndex: isSelected ? 1 : 0,
										"&:hover": onSegmentClick ? { backgroundColor: "#d5d5d5" } : undefined,
									}}
								/>
							</Tooltip>
						);
					})}
				</Box>
			</Box>

			<Box
				sx={{
					display: "flex",
					flexDirection: "row",
					width: "100%",
					mt: 0.5,
					minWidth: 0,
				}}>
				{segments.map((row, i) => {
					const pct = Number(row.percentage) || 0;
					return (
						<Box
							key={`lbl-${fashionabilityContributionRowKey(row)}-${i}`}
							sx={{
								flex: `0 1 ${pct}%`,
								minWidth: 0,
								px: 0.125,
								borderRight: i < segments.length - 1 ? `2px solid transparent` : "none",
								boxSizing: "border-box",
							}}>
							<Typography
								variant="caption"
								component="div"
								title={row.fashionability}
								sx={{
									textAlign: "center",
									color: labelColor,
									fontSize: "0.55rem",
									fontWeight: 600,
									lineHeight: 1.2,
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}>
								{formatLabelPct(pct)}%
							</Typography>
							<Typography
								variant="caption"
								component="div"
								title={row.fashionability}
								sx={{
									textAlign: "center",
									color: titleColor,
									fontSize: "0.5rem",
									fontWeight: 500,
									lineHeight: 1.15,
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
									mt: 0.15,
								}}>
								{row.fashionability}
							</Typography>
						</Box>
					);
				})}
			</Box>
		</Box>
	);
}
